import { externalLinksSchema } from "./externalLinks.schema";
import { languageSchema } from "./languageSchema";
import { makeI18nField } from "../utils/i18nSchemas";

/**
 * `sponsors.description` is a varchar(255), so every translation has to fit
 * inside it — the column is written from `descriptionI18n` on every write.
 */
export const SPONSOR_DESCRIPTION_MAX_LENGTH = 255;

const create = {
  type: "object",
  properties: {
    nameI18n: makeI18nField({ required: true, minLength: 1 }),
    levelId: { type: "number" },
    logo: { type: "string" },
    descriptionI18n: makeI18nField({
      maxLength: SPONSOR_DESCRIPTION_MAX_LENGTH,
    }),
    eventId: { type: "number" },
    repIds: { type: "array", items: { type: "number" } },
    links: { type: "array", items: externalLinksSchema },
    order: { type: "number", minimum: 0 },
  },
  required: ["nameI18n", "eventId", "levelId"],
};

export const createSponsorSchema = {
  ...create,
};

export const updateSponsorSchema = {
  ...create,
  required: [],
  minProperties: 1,
};

export const editSponsorLevelsSchema = {
  type: "object",
  properties: {
    eventId: { type: "number" },
    levels: {
      type: "array",
      items: {
        type: "object",
        properties: {
          nameI18n: makeI18nField({ required: true, minLength: 1 }),
          order: { type: "number" },
        },
        required: ["nameI18n", "order"],
      },
    },
  },
  required: ["levels", "eventId"],
};

export const getSponsorsSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      page: { type: "number" },
      limit: { type: "number" },
      sort: { type: "string" },
      filter: { type: "string" },
      ...languageSchema.properties,
    },
    required: ["eventId"],
  },
};

export const getSponsorByIdSchema = {
  querystring: languageSchema,
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

export const deleteSponsorSchema = {
  body: {
    type: "object",
    properties: { id: { type: "number" } },
    required: ["id"],
  },
};

export const reorderSponsorsSchema = {
  body: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      levelId: { type: "number" },
      sponsorIds: {
        type: "array",
        items: { type: "number" },
        minItems: 1,
      },
    },
    required: ["eventId", "levelId", "sponsorIds"],
  },
};

export const getSponsorLevelsSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      ...languageSchema.properties,
    },
    required: ["eventId"],
  },
};

export const web_sponsorFormSchema = {
  type: "object",
  properties: {
    nameI18n: makeI18nField({ required: true, minLength: 3 }),
    descriptionI18n: makeI18nField({
      maxLength: SPONSOR_DESCRIPTION_MAX_LENGTH,
    }),
    levelId: { type: "string", minLength: 1 },
    eventId: { type: "number" },
    links: {
      type: "array",
      items: {
        type: "object",
        properties: {
          type: { type: "string", enum: ["external", "social"] },
          title: { type: "string", minLength: 1 },
          platform: { type: ["string", "null"] },
          url: {
            type: "string",
            minLength: 1,
          },
        },
        required: ["type", "url"],
        allOf: [
          {
            if: {
              properties: { type: { const: "external" } },
              required: ["type"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^https?://.+$",
                  errorMessage: {
                    pattern:
                      "URL is not a valid external link (must start with http:// or https://)",
                  },
                },
              },
              required: ["title"],
            },
          },
          {
            if: {
              properties: { type: { const: "social" } },
              required: ["type"],
            },
            then: { required: ["platform"] },
          },
          // email
          {
            if: {
              properties: { platform: { type: "string", const: "email" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
                  errorMessage: {
                    pattern: "URL does not match the selected platform format",
                  },
                },
              },
            },
          },
          // linkedin
          {
            if: {
              properties: { platform: { type: "string", const: "linkedin" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^https://(www\\.)?linkedin\\.com(/.*)?$",
                  errorMessage: {
                    pattern: "URL does not match the selected platform format",
                  },
                },
              },
            },
          },
          // twitter/x
          {
            if: {
              properties: { platform: { type: "string", const: "twitter" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^https://(www\\.)?(twitter|x)\\.com(/.*)?$",
                  errorMessage: {
                    pattern: "URL does not match the selected platform format",
                  },
                },
              },
            },
          },
          // facebook
          {
            if: {
              properties: { platform: { type: "string", const: "facebook" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^https://(www\\.)?facebook\\.com(/.*)?$",
                  errorMessage: {
                    pattern: "URL does not match the selected platform format",
                  },
                },
              },
            },
          },
          // instagram
          {
            if: {
              properties: { platform: { type: "string", const: "instagram" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^https://(www\\.)?instagram\\.com(/.*)?$",
                  errorMessage: {
                    pattern: "URL does not match the selected platform format",
                  },
                },
              },
            },
          },
          // youtube
          {
            if: {
              properties: { platform: { type: "string", const: "youtube" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^https://(www\\.)?youtube\\.com(/.*)?$",
                  errorMessage: {
                    pattern: "URL does not match the selected platform format",
                  },
                },
              },
            },
          },
          // github
          {
            if: {
              properties: { platform: { type: "string", const: "github" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: {
                  type: "string",
                  pattern: "^https://(www\\.)?github\\.com(/.*)?$",
                  errorMessage: {
                    pattern: "URL does not match the selected platform format",
                  },
                },
              },
            },
          },
          // website
          {
            if: {
              properties: { platform: { type: "string", const: "website" } },
              required: ["platform"],
            },
            then: {
              properties: {
                url: { type: "string", pattern: "^https?://.+$" },
                errorMessage: {
                  pattern: "URL does not match the selected platform format",
                },
              },
            },
          },
        ],
      },
    },
    logo: { type: ["string", "null"] },
    repIds: { type: "array", items: { type: "string" } },
  },
  required: ["nameI18n", "levelId"],
  additionalProperties: false,
};
